export const calculateInitials = (label: string): string => {
  // strip special characters, keeping only letters, numbers and spaces
  const cleaned = label.replace(/[^a-zA-Z0-9\s]/g, '').trim();
  const words = cleaned.split(/\s+/).filter(Boolean);
  if (words.length === 0) return label.substring(0, 2).toUpperCase();
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  return words
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');
};

export interface WithInitials {
  initials?: string;
}

/**
 * Returns initials for a given item.
 * @param item - any object that may have an `initials` property
 * @param getLabel - function to resolve the display label from the item
 * @param displayInitials - whether to calculate initials from the label if not explicitly set
 */
export const getInitials = <T extends WithInitials>(
  item: T | null | undefined,
  getLabel: (item: T) => string,
  displayInitials = true
): string => {
  if (!item) return '';
  if (item.initials) return item.initials;
  if (displayInitials) {
    return calculateInitials(getLabel(item));
  }
  return '';
};

/**
 * Flattens a nested validation error object (e.g. { notEmpty: ["msg"] }) into a flat string array.
 */
export function flatErrors(errors: any): string[] {
  if (!errors || typeof errors !== 'object') return [];
  const result: string[] = [];
  const flatten = (obj: any): void => {
    Object.values(obj).forEach((value) => {
      if (Array.isArray(value)) {
        value.forEach((e) => result.push(String(e)));
      } else if (typeof value === 'object' && value !== null) {
        flatten(value);
      } else {
        result.push(String(value));
      }
    });
  };
  flatten(errors);
  return result;
}
